import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useLocale } from "@/contexts/LocaleContext";
import { CarouselDots } from "@/components/ui/carousel-dots";
import { attachCarouselTracking, trackCarouselInnerCollision } from "@/lib/carouselTrack";

function makeIgnoreInner(outerName: string) {
  return function ignoreDragFromInnerCarousel(_api: unknown, evt: PointerEvent | MouseEvent | TouchEvent) {
    const target = evt.target as HTMLElement | null;
    if (isInnerCarouselTarget(target)) {
      trackCarouselInnerCollision(outerName);
      return false;
    }
    return true;
  };
}

function isInnerCarouselTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && Boolean(target.closest("[data-inner-carousel]"));
}

/**
 * MobileCarousel — مصدر واحد للحقيقة لكلّ كاروسيلات الموبايل في الهوم.
 *
 * استراتيجيّة RTL الموحَّدة (الجديدة):
 *  - نمرّر `direction: "rtl" | "ltr"` لـEmbla مباشرة.
 *  - لا نعكس المصفوفة يدويّاً (كان يسبّب انعكاساً مزدوجاً).
 *  - نضع `dir` على الـviewport ليتطابق محور السحب مع توقّع المستخدم.
 *  - `scrollTo(i)` يستخدم الفهرس الأصليّ — Embla يعالج الانعكاس داخلياً.
 *
 * يحرسه `src/__tests__/mobile-carousels-rtl.test.ts`.
 */
export function MobileCarousel<T>({
  items,
  getKey,
  renderItem,
  align = "start",
  containerPadClass = "px-6",
  ariaPrefix = "slide",
  className = "",
  slideClassName = "flex-[0_0_85%]",
  onSlideChange,
}: {
  items: T[];
  getKey: (item: T, indexInVisible: number) => string;
  renderItem: (item: T, indexInVisible: number, isActive: boolean) => React.ReactNode;
  align?: "start" | "center";
  containerPadClass?: string;
  ariaPrefix?: string;
  className?: string;
  slideClassName?: string;
  /** يُستدعى عند تغيّر الشريحة النشطة (وأوّل mount أيضاً). */
  onSlideChange?: (index: number) => void;
}) {
  const { dir } = useLocale();
  const isRtl = dir === "rtl";

  // احترام prefers-reduced-motion: إلغاء انتقال السحب (السحب يبقى يعمل)
  const [reduceMotion, setReduceMotion] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  const ignoreInner = React.useMemo(() => makeIgnoreInner(ariaPrefix), [ariaPrefix]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align,
    containScroll: "trimSnaps",
    loop: false,
    // 18→14: استجابة أسرع للسحب الواضح بين البطاقات دون تفعيله عرضيّاً
    // (يبقى أعلى من حدّ النقر العاديّ 8px).
    dragThreshold: 14,
    // 28→22: انتقال أنعم بقليل، يقلّل إحساس «الاهتزاز» عند الإفلات بسرعة.
    duration: reduceMotion ? 0 : 22,
    skipSnaps: false,
    direction: isRtl ? "rtl" : "ltr",
    inViewThreshold: 0.6,
    watchDrag: ignoreInner,
  });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const onSlideChangeRef = React.useRef(onSlideChange);
  const dragClickGuardRef = React.useRef({
    x: 0,
    y: 0,
    innerCarousel: false,
    horizontalDrag: false,
    blockClick: false,
  });
  React.useEffect(() => {
    onSlideChangeRef.current = onSlideChange;
  }, [onSlideChange]);

  const onPointerDown = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragClickGuardRef.current = {
      x: e.clientX,
      y: e.clientY,
      innerCarousel: isInnerCarouselTarget(e.target),
      horizontalDrag: false,
      blockClick: false,
    };
  }, []);

  const onPointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragClickGuardRef.current.innerCarousel) return;
    const dx = Math.abs(e.clientX - dragClickGuardRef.current.x);
    const dy = Math.abs(e.clientY - dragClickGuardRef.current.y);
    if (dx > 10 && dx > dy) dragClickGuardRef.current.horizontalDrag = true;
  }, []);

  const onPointerUp = React.useCallback(() => {
    if (dragClickGuardRef.current.horizontalDrag) {
      dragClickGuardRef.current.blockClick = true;
    }
  }, []);

  const onClickCapture = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragClickGuardRef.current.blockClick) return;
    e.preventDefault();
    e.stopPropagation();
    dragClickGuardRef.current.blockClick = false;
  }, []);

  // إعادة تهيئة Embla عند تغيّر الاتجاه/الترتيب — لا بدّ من تمرير direction
  // الجديد لـ reInit صراحةً، لأنّه بدون options جديدة يحتفظ بالاتجاه الأصليّ
  // الذي تمّ تركيبه عند أوّل render (LTR قبل hydration).
  React.useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({
      align,
      containScroll: "trimSnaps",
      loop: false,
      dragThreshold: 14,
      duration: reduceMotion ? 0 : 22,
      skipSnaps: false,
      direction: isRtl ? "rtl" : "ltr",
      inViewThreshold: 0.6,
      watchDrag: ignoreInner,
    });
    emblaApi.scrollTo(0, true);
    setSelectedIndex(0);
  }, [emblaApi, isRtl, items.length, align, ignoreInner, reduceMotion]);

  // تتبّع GA4: نسبة نجاح/إلغاء السحب لكلّ كاروسيل
  React.useEffect(() => attachCarouselTracking(emblaApi, ariaPrefix), [emblaApi, ariaPrefix]);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const idx = emblaApi.selectedScrollSnap();
      setSelectedIndex(idx);
      onSlideChangeRef.current?.(idx);
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  // تنقّل لوحة المفاتيح: أسهم + Home/End. يحترم RTL.
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!emblaApi) return;
      const key = e.key;
      if (key === "ArrowRight" || key === "ArrowLeft") {
        e.preventDefault();
        const next = isRtl ? key === "ArrowLeft" : key === "ArrowRight";
        if (next) emblaApi.scrollNext();
        else emblaApi.scrollPrev();
      } else if (key === "Home") {
        e.preventDefault();
        emblaApi.scrollTo(0);
      } else if (key === "End") {
        e.preventDefault();
        emblaApi.scrollTo(items.length - 1);
      }
    },
    [emblaApi, isRtl, items.length],
  );

  const ofLabel = isRtl ? "من" : "of";
  const slideWord = isRtl ? "شريحة" : "slide";
  const liveLabel = `${ariaPrefix} ${slideWord} ${selectedIndex + 1} ${ofLabel} ${items.length}`;

  return (
    <div className={className}>
      <div
        ref={emblaRef}
        dir={dir}
        role="region"
        aria-roledescription={isRtl ? "كاروسيل" : "carousel"}
        aria-label={ariaPrefix}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
        className="overflow-hidden touch-pan-y [overscroll-behavior-x:contain] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-none"
      >
        <div className={`flex items-stretch gap-3 ${containerPadClass} py-4`}>
          {items.map((item, i) => (
            <div
              key={getKey(item, i)}
              role="group"
              aria-roledescription={slideWord}
              aria-label={`${i + 1} ${ofLabel} ${items.length}`}
              aria-hidden={selectedIndex !== i}
              className={`relative min-w-0 ${slideClassName}`}
            >
              {renderItem(item, i, selectedIndex === i)}
            </div>
          ))}
        </div>
      </div>

      {/* إعلان تغيّر الشريحة لقارئ الشاشة */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveLabel}
      </div>

      <CarouselDots
        emblaApi={emblaApi}
        count={items.length}
        ariaPrefix={ariaPrefix}
        variant="default"
        className="mt-4"
      />
    </div>
  );
}
